import Phaser from 'phaser';

/**
 * Onboarding hint that flashes a 4-keycap keyboard cross (↑ ← ↓ →) +
 * caption during the 3-2-1 countdown so first-time players realize they
 * have to drive the snake with the keyboard.
 *
 * One arrow lights up at a time, cycling UP→RIGHT→DOWN→LEFT, to telegraph
 * "these are your inputs". Position is fixed near the bottom-center of
 * the screen so it doesn't compete with the countdown numbers.
 *
 * Owned by GameScene — call `show()` when the countdown starts and
 * `hide()` on GO!.
 */
export class KeyboardHintOverlay {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private keys: Phaser.GameObjects.Container[] = [];
  private cycleTimer: Phaser.Time.TimerEvent | null = null;
  private currentIdx: number = 0;
  /** Cycle order: UP, RIGHT, DOWN, LEFT */
  private static readonly CYCLE_ORDER = [0, 3, 2, 1];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const { width, height } = scene.scale;

    this.container = scene.add.container(width / 2, height * 0.78)
      .setScrollFactor(0)
      .setDepth(199)
      .setAlpha(0);

    // Keycap layout — cross shape. Indices: 0=UP, 1=LEFT, 2=DOWN, 3=RIGHT
    const KEY_SIZE = 38;
    const KEY_GAP = 6;
    const layout = [
      { glyph: '↑', x: 0,                       y: -(KEY_SIZE + KEY_GAP) / 2 - 2 },
      { glyph: '←', x: -(KEY_SIZE + KEY_GAP),   y:  (KEY_SIZE + KEY_GAP) / 2 - 2 },
      { glyph: '↓', x: 0,                       y:  (KEY_SIZE + KEY_GAP) / 2 - 2 },
      { glyph: '→', x:  (KEY_SIZE + KEY_GAP),   y:  (KEY_SIZE + KEY_GAP) / 2 - 2 },
    ];

    for (const cap of layout) {
      const keycap = this.makeKeycap(cap.x, cap.y, cap.glyph, KEY_SIZE);
      this.keys.push(keycap);
      // Parent the keycap to the overlay container so its layout coords
      // (e.g. (0, -22), (-44, 20)) are RELATIVE to the container origin
      // — without this they'd be world-space and end up at top-left.
      this.container.add(keycap);
    }

    // Caption — sits below the cross
    const caption = scene.add.text(0, KEY_SIZE * 1.6, 'MOVE WITH ARROWS  /  WASD', {
      fontFamily: 'monospace',
      fontSize: '12px',
      color: '#aab4c8',
      stroke: '#000000',
      strokeThickness: 3,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.container.add(caption);
  }

  private makeKeycap(x: number, y: number, glyph: string, size: number): Phaser.GameObjects.Container {
    const cap = this.scene.add.container(x, y);

    // Background — rounded rectangle drawn via Graphics
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x14141e, 0.85);
    bg.fillRoundedRect(-size / 2, -size / 2, size, size, 6);
    bg.lineStyle(2, 0x4a5568, 1);
    bg.strokeRoundedRect(-size / 2, -size / 2, size, size, 6);

    const text = this.scene.add.text(0, 0, glyph, {
      fontFamily: 'monospace',
      fontSize: '22px',
      color: '#aab4c8',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    cap.add([bg, text]);
    cap.setData('bg', bg);
    cap.setData('text', text);
    cap.setData('size', size);
    return cap;
  }

  /** Fade in + start cycling the active-key highlight. */
  show(): void {
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: 250,
      ease: 'Sine.easeOut',
    });
    this.currentIdx = 0;
    this.applyHighlight(KeyboardHintOverlay.CYCLE_ORDER[0]);
    this.cycleTimer = this.scene.time.addEvent({
      delay: 400,
      loop: true,
      callback: () => {
        this.currentIdx = (this.currentIdx + 1) % KeyboardHintOverlay.CYCLE_ORDER.length;
        this.applyHighlight(KeyboardHintOverlay.CYCLE_ORDER[this.currentIdx]);
      },
    });
  }

  /** Fade out, stop cycle, then destroy. */
  hide(): void {
    this.cycleTimer?.remove(false);
    this.cycleTimer = null;
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: 250,
      ease: 'Sine.easeIn',
      onComplete: () => this.destroy(),
    });
  }

  destroy(): void {
    this.cycleTimer?.remove(false);
    this.cycleTimer = null;
    this.container.destroy(true);
  }

  /** Light up keycap `activeIdx`, dim the rest. Includes a brief scale pulse. */
  private applyHighlight(activeIdx: number): void {
    for (let i = 0; i < this.keys.length; i++) {
      const cap = this.keys[i];
      const bg = cap.getData('bg') as Phaser.GameObjects.Graphics;
      const text = cap.getData('text') as Phaser.GameObjects.Text;
      const size = cap.getData('size') as number;
      const isActive = i === activeIdx;

      bg.clear();
      bg.fillStyle(isActive ? 0xffd700 : 0x14141e, isActive ? 0.95 : 0.85);
      bg.fillRoundedRect(-size / 2, -size / 2, size, size, 6);
      bg.lineStyle(2, isActive ? 0xffaa00 : 0x4a5568, 1);
      bg.strokeRoundedRect(-size / 2, -size / 2, size, size, 6);
      text.setColor(isActive ? '#1a1408' : '#aab4c8');

      if (isActive) {
        cap.setScale(1);
        this.scene.tweens.add({
          targets: cap,
          scale: 1.15,
          duration: 120,
          yoyo: true,
          ease: 'Sine.easeOut',
        });
      } else {
        this.scene.tweens.killTweensOf(cap);
        cap.setScale(1);
      }
    }
  }
}
